<template>
  <checkout-form
    :class="['checkout-option-form', { 'no-radio': disableRadio }]"
    :controls="controls"
    eventsOnly
    @form:change="onChange"
    @form:submit="onChange"
    v-slot="formSlotProps"
  >
    <b-form-radio
      v-if="!disableRadio"
      class="option-form-control"
      v-model="stepContext.optionsActive[name]"
      size="lg"
      :name="name"
      :value="value"
      @change="onRadioChange"
    >{{ text }}</b-form-radio>

    <div class="option-form-content" v-show="isActive && $scopedSlots.default">
      <slot v-bind="formSlotProps"/>
    </div>
  </checkout-form>
</template>

<script>
import CheckoutForm from '@/components/public/forms/Form'
import CheckoutStep from '@/components/public/order/CheckoutStep'

import { getNamedParentContext } from '@/assets/js/helper/vm'

export default {
  name: 'CheckoutOptionForm',
  components: {
    CheckoutForm
  },
  props: {
    name: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number, Boolean],
      required: true
    },
    text: {
      type: String,
      required: true
    },
    controls: {
      type: Object,
      default: () => ({})
    },
    disableGtmOnOptionChange: {
      type: Boolean,
      default: false
    },
    emitChangesDirectly: {
      type: Boolean,
      default: false
    },
    disableRadio: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      form: null
    }
  },
  computed: {
    isActive () {
      return this.stepContext.optionsActive[this.name] === this.value
    },
    optionFormData () {
      return Object.assign({ option: { name: this.name, value: this.value } }, this.form)
    }
  },
  methods: {
    updateControl ({ key = '', value }) {
      if (this.form.controls[key]) {
        this.form.controls[key].value = value
      }
    },
    onChange (form = null) {
      this.form = form

      if (this.isActive) {
        const context = !this.emitChangesDirectly ? this.stepContext : this
        context.$emit('optionform:change', this.optionFormData)
      }
    },
    onRadioChange (form = null) {
      if (!this.disableGtmOnOptionChange) {
        this.stepContext.$emit('optionform:enablegtm')
      }
    }
  },
  beforeCreate () {
    this.stepContext = getNamedParentContext(CheckoutStep.name, this)
  },
  created () {
    this.$on('control:change', this.updateControl)
  },
  watch: {
    isActive (is) {
      if (is) {
        this.onChange(this.form)
      }
    }
  }
}
</script>

<style lang="scss">
$checkoutoptionform-padding-y: $spacer * 1.25 !default;
$checkoutoptionform-padding-x: $checkoutoptionform-padding-y !default;
$checkoutoptionform-border: map-get($borders, 'bold') !default;
$checkoutoptionform-border-radius: $border-radius-lg !default;

$checkoutoptionform-control-font-size: $font-size-lg !default;
$checkoutoptionform-control-font-weight: $font-weight-bold !default;

$checkoutoptionform-content-offset: $b-custom-control-indicator-size-lg + $custom-control-gutter !default;
$checkoutoptionform-content-gap: $spacer * 0.5 !default;
$checkoutoptionform-content-padding-y: 0 !default;
$checkoutoptionform-content-padding-x: 0 !default;

.checkout-option-form {
  padding: $checkoutoptionform-padding-y $checkoutoptionform-padding-x;
  border: $checkoutoptionform-border;
  border-radius: $checkoutoptionform-border-radius;

  .option-form-control {
    font-size: $checkoutoptionform-control-font-size;
    font-weight: $checkoutoptionform-control-font-weight;

    .custom-control-input {
      ~ .custom-control-label {
        margin: 0;
      }
    }
  }

  .option-form-content {
    margin-left: $checkoutoptionform-content-offset;
    margin-top: $checkoutoptionform-content-gap;
    padding: $checkoutoptionform-content-padding-y $checkoutoptionform-content-padding-x;
  }

  &.no-radio {
    .option-form-content {
      margin-left: 0;
    }
  }

  + .checkout-option-form {
    border-top: 0 none;
  }

  &:not(:first-child) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  &:not(:last-child) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}
</style>
